import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import ExplorerHeader from '../components/explorer/ExplorerHeader';
import FilterSidebar from '../components/explorer/FilterSidebar';
import RecipeGrid from '../components/explorer/RecipeGrid';
import PaginationControls from '../components/explorer/PaginationControls';
import GenerateRecipePanel from '../components/explorer/GenerateRecipePanel';
import { deleteRecipe, getRecipes, getUserFavorites, addToFavorites, removeFromFavorites } from '../services/recipeService';
import { getRecipeShareText } from '../utils/shareUtils';
import { useLanguage } from '../i18n/LanguageContext';
import './ExplorerPage.css';

const PAGE_SIZE = 10;

const ExplorerPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, onSubmitNavigate, onAuthRequiredMessage } = useOutletContext() ?? {};
  const { lang, t } = useLanguage();
  const modeFromQuery = searchParams.get('mode') === 'generate' ? 'generate' : 'search';
  const [activeMode, setActiveMode] = useState(modeFromQuery);
  const [recipes, setRecipes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeActionId, setActiveActionId] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState('title');

  const [activeTags, setActiveTags] = useState(new Set());

  useEffect(() => {
    setActiveMode(modeFromQuery);
  }, [modeFromQuery]);

  // Share handler
  const handleShare = (recipe) => {
    const shareText = getRecipeShareText(recipe, lang);
    const url = window.location.origin + window.location.pathname + (lang === 'he' ? `#/heb/recipes/${recipe._id || recipe.id}` : `#/recipes/${recipe._id || recipe.id}`);
    if (navigator.share) {
      navigator.share({
        title: recipe.title,
        text: shareText,
        url,
      });
    } else {
      // Fallback: open a simple dialog with share links
      const whatsapp = `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + url)}`;
      const mailto = `mailto:?subject=${encodeURIComponent(recipe.title)}&body=${encodeURIComponent(shareText + '\n' + url)}`;
      window.open(whatsapp, '_blank');
      // Optionally, show a modal with more options
    }
  };

  const handleTagToggle = (tag) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      return next;
    });
    setCurrentPage(1);
  };

  const isAdmin = currentUser?.role === 'ADMIN';
  const canManageRecipe = (recipe) =>
    isAdmin || (currentUser && currentUser.id === recipe?.recipeUserId);

  const handleModeChange = (mode) => {
    if (mode === 'generate' && !currentUser) {
      onAuthRequiredMessage?.(t.explorer.generateLoginRequired);
      return;
    }

    setActiveMode(mode);
    const nextParams = new URLSearchParams(searchParams);
    if (mode === 'generate') {
      nextParams.set('mode', 'generate');
    } else {
      nextParams.delete('mode');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleGenerateEntryClick = () => {
    if (!currentUser) {
      onAuthRequiredMessage?.(t.explorer.generateLoginRequired);
      return;
    }

    handleModeChange('generate');
  };

  const headerContent = {
    eyebrow: t.explorer.eyebrow,
    title: t.explorer.title,
    accent: t.explorer.accent,
    description: t.explorer.description,
  };

  const generateLabels = {
    dir: lang === 'he' ? 'rtl' : undefined,
    generateTitle: t.explorer.generateTitle,
    generateDescription: t.explorer.generateDescription,
    generateInputPlaceholder: t.explorer.generateInputPlaceholder,
    generateAddIngredient: t.explorer.generateAddIngredient,
    generateRemoveIngredient: t.explorer.generateRemoveIngredient,
    generateLoadingSuggestions: t.explorer.generateLoadingSuggestions,
    generateHint: t.explorer.generateHint,
    generateSubmit: t.explorer.generateSubmit,
    generateSubmitting: t.explorer.generateSubmitting,
    generateMinIngredientsError: t.explorer.generateMinIngredientsError,
    generateLoginRequired: t.explorer.generateLoginRequired,
    generateQuotaError: t.explorer.generateQuotaError,
    generateUnexpectedError: t.explorer.generateUnexpectedError,
    generateIngredientsTitle: t.explorer.generateIngredientsTitle,
    generateStepsTitle: t.explorer.generateStepsTitle,
    generateNutritionTitle: t.explorer.generateNutritionTitle,
    generateNutritionCalories: t.nutrition.calories,
    generateNutritionProtein: t.nutrition.protein,
    generateNutritionCarbs: t.nutrition.carbs,
    generateNutritionFat: t.nutrition.fat,
    generatePreviewTitle: t.explorer.generatePreviewTitle,
    generateSourceRepository: t.explorer.generateSourceRepository,
    generateSourceAi: t.explorer.generateSourceAi,
    generateSaveButton: t.explorer.generateSaveButton,
    generateSaving: t.explorer.generateSaving,
    generateSaveSuccess: t.explorer.generateSaveSuccess,
    generateSaveError: t.explorer.generateSaveError,
  };

  const handleRecipeSelect = (recipe) => {
    const recipeId = recipe?._id || recipe?.id;
    if (!recipeId) {
      return;
    }
    const path = lang === 'he' ? `/heb/recipes/${recipeId}` : `/recipes/${recipeId}`;
    navigate(path, { state: { recipe } });
  };

  const handleRecipeEdit = (recipe) => {
    if (!canManageRecipe(recipe)) {
      return;
    }

    const submitPath = lang === 'he' ? '/heb/submit' : '/submit';
    navigate(submitPath, { state: { recipe, mode: 'edit' } });
  };

  const handleRecipeDelete = async (recipe) => {
    const recipeId = recipe?._id || recipe?.id;

    if (!canManageRecipe(recipe) || !recipeId) {
      return;
    }

    const shouldDelete = typeof window === 'undefined' || window.confirm(`Delete "${recipe.title}"?`);

    if (!shouldDelete) {
      return;
    }

    setActiveActionId(recipeId);

    try {
      await deleteRecipe(recipeId);
      setRecipes((prev) => prev.filter((entry) => (entry?._id || entry?.id) !== recipeId));
      setError(null);
    } catch (err) {
      setError(err?.message ?? 'Unable to delete this recipe.');
    } finally {
      setActiveActionId(null);
    }
  };

  const handleFavoriteToggle = async (recipe) => {
    const recipeId = recipe._id || recipe.id;
    const isFav = favoriteIds?.has(recipeId);

    // Optimistic update
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFav) {
        next.delete(recipeId);
      } else {
        next.add(recipeId);
      }
      return next;
    });

    try {
      if (isFav) {
        await removeFromFavorites(recipeId);
      } else {
        await addToFavorites(recipeId);
      }
    } catch {
      // Revert on failure
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) {
          next.add(recipeId);
        } else {
          next.delete(recipeId);
        }
        return next;
      });
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const loadRecipes = async () => {
      setIsLoading(true);
      try {
        const result = await getRecipes(
          { page: currentPage, pageSize: PAGE_SIZE, search: searchQuery, searchScope, lang, tags: [...activeTags] },
          controller.signal
        );
        if (!isMounted) return;
        setRecipes(result.recipes);
        setTotalPages(result.totalPages);
        setError(null);
      } catch (err) {
        if (!isMounted || controller.signal.aborted) return;
        setError(err?.message ?? 'Unable to load recipes.');
      } finally {
        if (isMounted && !controller.signal.aborted) setIsLoading(false);
      }
    };

    loadRecipes();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [currentPage, searchQuery, searchScope, lang, activeTags]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(1);
  }, [lang]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, searchScope]);

  useEffect(() => {
    if (!currentUser) {
      setFavoriteIds(null);
      return undefined;
    }

    let isMounted = true;
    getUserFavorites()
      .then((data) => {
        if (!isMounted) return;
        setFavoriteIds(new Set((data ?? []).map((r) => r._id || r.id)));
      })
      .catch(() => {
        if (isMounted) setFavoriteIds(new Set());
      });

    return () => { isMounted = false; };
  }, [currentUser]);

  return (
    <div className="explorer-page">
      <main className="explorer-main">
        <div className="explorer-header-row">
          <ExplorerHeader {...headerContent} />
          <div className="explorer-header-actions">
             <button
              type="button"
              className={`explorer-add-btn${!currentUser ? ' is-disabled' : ''}`}
              onClick={onSubmitNavigate}
            >
              {t.explorer.addRecipe}
            </button>
            <button
              type="button"
              className={`explorer-add-btn${!currentUser ? ' is-disabled' : ''}`}
              onClick={handleGenerateEntryClick}
            >
              {t.explorer.generateEntryPoint}
            </button>
          </div>
        </div>
        {activeMode === 'search' ? (
          <>
            <div className="explorer-search-bar" dir={lang === 'he' ? 'rtl' : undefined}>
              <span className="material-symbols-rounded explorer-search-icon" aria-hidden="true">search</span>
              <input
                className="explorer-search-input"
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.explorer.searchPlaceholder}
                aria-label={t.explorer.searchPlaceholder}
              />
              <div className="explorer-search-scope">
                <button
                  type="button"
                  className={`explorer-scope-btn${searchScope === 'title' ? ' is-active' : ''}`}
                  onClick={() => setSearchScope('title')}
                >
                  {t.explorer.searchScopeTitle}
                </button>
                <button
                  type="button"
                  className={`explorer-scope-btn${searchScope === 'content' ? ' is-active' : ''}`}
                  onClick={() => setSearchScope('content')}
                >
                  {t.explorer.searchScopeContent}
                </button>
              </div>
            </div>
            <div className="explorer-layout">
              <FilterSidebar activeTags={activeTags} onTagToggle={handleTagToggle} />
              <div className="explorer-content">
                  <RecipeGrid
                    recipes={recipes}
                    isLoading={isLoading}
                    error={error}
                    onRecipeSelect={handleRecipeSelect}
                    onRecipeEdit={handleRecipeEdit}
                    onRecipeDelete={handleRecipeDelete}
                    activeActionId={activeActionId}
                    currentUser={currentUser}
                    favoriteIds={favoriteIds}
                    onFavoriteToggle={currentUser ? handleFavoriteToggle : null}
                    emptyMessage={searchQuery.trim() ? t.explorer.searchNoResults : null}
                    onShare={handleShare}
                />
                {totalPages > 1 && (
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    lang={lang}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="explorer-generate-layout">
            <GenerateRecipePanel
              currentUser={currentUser}
              labels={generateLabels}
              onRecipeSelect={handleRecipeSelect}
            />
          </div>
        )}
      </main>
      <PageFooter />
    </div>
  );
};

export default ExplorerPage;
